import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where
} from "firebase/firestore";
import {
  FiActivity,
  FiCalendar,
  FiCamera,
  FiChevronDown,
  FiChevronUp,
  FiDollarSign,
  FiExternalLink,
  FiHeart,
  FiLayers,
  FiMapPin,
  FiShare2,
  FiTool,
  FiUser,
  FiZap
} from "react-icons/fi";
import { auth, db } from "./firebase";
import PageLayout from "./components/PageLayout";
import BuildHistoryMenu from "./components/BuildHistoryMenu";
import Username from "./components/Username";
import {
  VEHICLE_SPEC_FIELDS,
  getCurrentStage,
  normalizeCarStages,
  normalizeVehicleSpecs
} from "./utils/buildStages";

function CarPage() {
  const { username, carId } = useParams();

  const [profile, setProfile] = useState(null);
  const [car, setCar] = useState(null);
  const [pageError, setPageError] = useState("");
  const [loading, setLoading] = useState(true);
  const [isOwner, setIsOwner] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [savingBuild, setSavingBuild] = useState(false);
  const [viewerProfileId, setViewerProfileId] = useState("");
  const [toast, setToast] = useState("");
  const [modsOpen, setModsOpen] = useState(true);
  const [legacyModsOpen, setLegacyModsOpen] = useState(true);
  const [openCategories, setOpenCategories] = useState({});
  const [fullscreenImage, setFullscreenImage] = useState(null);

  useEffect(() => {
    const loadCarPage = async () => {
      try {
        setLoading(true);
        setPageError("");

        if (!username || !carId) {
          setPageError("Car not found.");
          setLoading(false);
          return;
        }

        const profileRef = doc(db, "profiles", username.toLowerCase());
        const profileSnap = await getDoc(profileRef);

        if (!profileSnap.exists()) {
          setPageError("Car not found.");
          setLoading(false);
          return;
        }

        const profileData = profileSnap.data();
        const foundCar = profileData.garageCars?.find(
          (item) => String(item.id) === String(carId)
        );

        if (!foundCar) {
          setPageError("Car not found.");
          setLoading(false);
          return;
        }

        const normalizedCar = normalizeCarStages(foundCar);

        setProfile({
          ...profileData,
          username: profileData.username || username
        });
        setCar(normalizedCar);

        const currentUser = auth.currentUser;
        const ownerMatch = Boolean(
          currentUser && currentUser.uid === profileData.userId
        );

        setIsOwner(ownerMatch);

        if (currentUser) {
          await loadViewerSaveState(currentUser.uid, username, carId);
        }
      } catch (error) {
        console.log("Car page load failed:", error);
        setPageError("Error loading build.");
      } finally {
        setLoading(false);
      }
    };

    loadCarPage();
  }, [username, carId]);

  const loadViewerSaveState = async (userId, ownerUsername, savedCarId) => {
    try {
      const profilesRef = collection(db, "profiles");
      const profileQuery = query(profilesRef, where("userId", "==", userId));
      const profileSnap = await getDocs(profileQuery);

      if (profileSnap.empty) return "";

      const viewerDoc = profileSnap.docs[0];
      const viewerData = viewerDoc.data();
      const savedBuilds = viewerData.savedBuilds || viewerData.favorites || [];
      const savedKey = getSavedBuildKey(ownerUsername, savedCarId);

      const matchingSavedBuild = savedBuilds.find((item) => {
        if (typeof item === "string") {
          return item === savedKey;
        }

        return (
          String(item?.username || item?.ownerUsername || "").toLowerCase() ===
            String(ownerUsername).toLowerCase() &&
          String(item?.carId) === String(savedCarId)
        );
      });

      setViewerProfileId(viewerDoc.id);
      setIsSaved(Boolean(matchingSavedBuild));

      return viewerDoc.id;
    } catch (error) {
      console.log("Saved build state failed:", error);
      return "";
    }
  };

  const currentPhase = useMemo(() => {
    if (!car) return null;
    return getCurrentStage(car);
  }, [car]);

  const vehicleTitle = useMemo(() => {
    if (!car) return "Vehicle Build";

    return (
      car.name ||
      `${car.year || ""} ${car.make || ""} ${car.model || ""}`.trim() ||
      "Vehicle Build"
    );
  }, [car]);

  const totalMods = car?.mods?.length || 0;
  const heroImage = car?.images?.[0] || null;
  const galleryImages = heroImage ? car?.images?.slice(1) || [] : car?.images || [];
  const statusLabel = car?.status || car?.buildStatus || "Driven";
  const profileLocation = getProfileLocation(profile);
  const updatedText = formatRelativeUpdatedDate(
    car?.lastUpdated || currentPhase?.updatedAt || car?.updatedAt || car?.createdAt
  );

  const groupedMods = useMemo(() => {
    const groups = {};

    (car?.mods || []).forEach((mod) => {
      const category = mod.category || "Other";
      if (!groups[category]) groups[category] = [];
      groups[category].push(mod);
    });

    return groups;
  }, [car]);

  const totalBuildCost = useMemo(() => {
    return (car?.mods || []).reduce((total, mod) => {
      return total + getModInvestment(mod);
    }, 0);
  }, [car]);

  const statistics = useMemo(
    () => buildStatistics(car, totalBuildCost, profile?.username),
    [car, totalBuildCost, profile?.username]
  );

  const vehicleSpecRows = useMemo(() => {
    const oem = normalizeVehicleSpecs(car?.oemSpecs);
    const current = normalizeVehicleSpecs(currentPhase?.specs);

    return VEHICLE_SPEC_FIELDS.map(({ key, label }) => ({
      key,
      label,
      oemValue: oem[key],
      currentValue: current[key]
    })).filter((row) => row.oemValue || row.currentValue);
  }, [car?.oemSpecs, currentPhase?.specs]);

  const legacyModSections = useMemo(() => {
    if (!car) return [];

    return [
      { title: "Performance", value: car.performanceMods || car.engineMods },
      { title: "Suspension", value: car.suspensionMods },
      { title: "Wheels & Tires", value: car.wheelTireMods || car.wheelMods },
      { title: "Exterior", value: car.exteriorMods },
      { title: "Interior", value: car.interiorMods },
      { title: "Miscellaneous", value: car.miscMods }
    ].filter((section) => hasText(section.value));
  }, [car]);

  const showToast = (message) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 2600);
  };

  const toggleSave = async () => {
    try {
      const currentUser = auth.currentUser;

      if (!currentUser) {
        showToast("Sign in to save this build.");
        return;
      }

      let activeViewerProfileId = viewerProfileId;

      if (!activeViewerProfileId) {
        activeViewerProfileId = await loadViewerSaveState(
          currentUser.uid,
          username,
          carId
        );
      }

      if (!activeViewerProfileId) {
        showToast("Could not save this build yet.");
        return;
      }

      setSavingBuild(true);

      const viewerRef = doc(db, "profiles", activeViewerProfileId);

      if (isSaved) {
        const viewerSnap = await getDoc(viewerRef);
        const viewerData = viewerSnap.exists() ? viewerSnap.data() : {};
        const savedBuilds = viewerData.savedBuilds || [];
        const favorites = viewerData.favorites || [];
        const savedKey = getSavedBuildKey(profile.username, car.id);

        await updateDoc(viewerRef, {
          savedBuilds: savedBuilds.filter(
            (item) => !isSameSavedBuild(item, savedKey, profile.username, car.id)
          ),
          favorites: favorites.filter(
            (item) => !isSameSavedBuild(item, savedKey, profile.username, car.id)
          )
        });

        setIsSaved(false);
        showToast("Build removed from saved builds.");
      } else {
        const newSavedBuild = {
          username: profile.username,
          carId: String(car.id),
          title: vehicleTitle,
          image: car.images?.[0] || "",
          savedAt: new Date().toISOString()
        };

        await updateDoc(viewerRef, {
          savedBuilds: arrayUnion(newSavedBuild)
        });

        setIsSaved(true);
        showToast("Build saved.");
      }
    } catch (error) {
      console.log("Save build failed:", error);
      showToast("Could not update saved builds.");
    } finally {
      setSavingBuild(false);
    }
  };

  const shareBuild = async () => {
    const shareUrl = window.location.href;
    const customBuildName = String(car?.buildName || car?.name || "").trim();
    const vehicleDescription = [car?.year, car?.make, car?.model]
      .map((value) => String(value || "").trim())
      .filter(Boolean)
      .join(" ");
    const sharedBuildName = customBuildName || vehicleDescription || "vehicle build";
    const shareMessage = `🚗 Check out my ${sharedBuildName} on MyBuildID. Here's my complete build, mods, photos, and specs.`;

    try {
      if (navigator.share) {
        await navigator.share({
          title: vehicleTitle,
          text: shareMessage,
          url: shareUrl
        });
      } else {
        await navigator.clipboard.writeText(`${shareMessage}\n\n${shareUrl}`);
        showToast("Build link copied.");
      }
    } catch (error) {
      if (error?.name !== "AbortError") {
        console.log("Share failed:", error);
        showToast("Could not share this build.");
      }
    }
  };

  const handlePhaseSelect = (phaseOrId) => {
    if (!car) return;

    const selectedPhase =
      typeof phaseOrId === "object"
        ? phaseOrId
        : car.stages?.find((stage) => String(stage.id) === String(phaseOrId));

    if (!selectedPhase) return;

    setCar({
      ...car,
      currentStageId: selectedPhase.id,
      images: selectedPhase.images || [],
      mods: selectedPhase.mods || [],
      timeline: selectedPhase.timeline || [],
      maintenance: selectedPhase.maintenance || [],
      specs: normalizeVehicleSpecs(selectedPhase.specs),
      performanceMods: selectedPhase.performanceMods || "",
      suspensionMods: selectedPhase.suspensionMods || "",
      wheelTireMods: selectedPhase.wheelTireMods || "",
      exteriorMods: selectedPhase.exteriorMods || "",
      interiorMods: selectedPhase.interiorMods || "",
      miscMods: selectedPhase.miscMods || "",
      stageName: selectedPhase.name || car.stageName || ""
    });
  };

  const toggleCategory = (category) => {
    setOpenCategories((current) => ({
      ...current,
      [category]: !current[category]
    }));
  };

  if (loading) {
    return (
      <PageLayout maxWidth="820px">
        <div style={stateCardStyle}>Loading build...</div>
      </PageLayout>
    );
  }

  if (pageError) {
    return (
      <PageLayout maxWidth="820px">
        <div style={stateCardStyle}>{pageError}</div>
      </PageLayout>
    );
  }

  if (!car || !profile) {
    return (
      <PageLayout maxWidth="820px">
        <div style={stateCardStyle}>Car not found.</div>
      </PageLayout>
    );
  }

  return (
    <PageLayout maxWidth="820px">
      <div style={pageShellStyle}>
        {toast && <div style={toastStyle}>{toast}</div>}

        <section
          style={{
            ...heroStyle,
            backgroundImage: heroImage
              ? `${heroImageGradient}, url("${heroImage}")`
              : fallbackHeroGradient
          }}
        >
          <div style={heroContentStyle}>
            <div style={heroStatusRowStyle}>
              <span style={getStatusBadgeStyle(statusLabel)}>
                {String(statusLabel).toUpperCase()}
              </span>
            </div>

            <h1 style={vehicleTitleStyle}>{vehicleTitle}</h1>

            <div style={heroMetaStyle}>
              {profileLocation && (
                <span style={heroMetaItemStyle}>
                  <FiMapPin aria-hidden="true" style={heroMetaIconStyle} />
                  {profileLocation}
                </span>
              )}

              <span style={heroMetaItemStyle}>
                <FiTool aria-hidden="true" style={heroMetaIconStyle} />
                {totalMods} {totalMods === 1 ? "Mod" : "Mods"}
              </span>
            </div>

            <div style={ownerRowStyle}>
              <FiUser aria-hidden="true" style={ownerIconStyle} />
              <span>
                Owned by{" "}
                <Link to={`/user/${profile.username}`} style={inlineLinkStyle}>
                  <Username username={profile.username} profile={profile} />
                </Link>
              </span>
              {updatedText && <span style={updatedTextStyle}>{updatedText}</span>}
            </div>
          </div>
        </section>

        <section style={actionBarStyle} aria-label="Build actions">
          <button
            type="button"
            onClick={toggleSave}
            disabled={savingBuild}
            style={{
              ...actionButtonStyle,
              opacity: savingBuild ? 0.65 : 1,
              cursor: savingBuild ? "not-allowed" : "pointer"
            }}
          >
            <FiHeart
              aria-hidden="true"
              style={isSaved ? actionIconActiveStyle : actionIconStyle}
            />
            <span>{isSaved ? "Saved" : "Save"}</span>
          </button>

          <button type="button" onClick={shareBuild} style={actionButtonStyle}>
            <FiShare2 aria-hidden="true" style={actionIconStyle} />
            <span>Share</span>
          </button>

          {isOwner && (
            <Link
              to={`/user/${profile.username}/car/${car.id}/show-tools`}
              style={actionLinkStyle}
            >
              <FiZap aria-hidden="true" style={actionIconStyle} />
              <span>Show Tools</span>
            </Link>
          )}
        </section>

        <section style={compactCardStyle}>
          <div style={phaseHeaderStyle}>
            <FiLayers aria-hidden="true" style={sectionIconStyle} />
            <span style={phaseLabelStyle}>Current Build Phase</span>
          </div>

          <div style={phaseMenuWrapStyle}>
            <BuildHistoryMenu
              car={car}
              stages={car.stages || []}
              currentStageId={car.currentStageId}
              setCar={setCar}
              onSelectStage={handlePhaseSelect}
              onStageChange={handlePhaseSelect}
              onSelectPhase={handlePhaseSelect}
            />
          </div>
        </section>

        {hasText(car.details) && (
          <section style={cardStyle}>
            <h2 style={sectionTitleStyle}>About This Build</h2>
            <p style={aboutTextStyle}>{car.details}</p>
          </section>
        )}

        {vehicleSpecRows.length > 0 && (
          <section style={cardStyle}>
            <h2 style={sectionTitleStyle}>Vehicle Specs</h2>

            <div style={specComparisonStyle}>
              <div style={specHeaderRowStyle}>
                <span style={specHeaderLabelStyle}>Spec</span>
                <span style={specHeaderValueStyle}>OEM</span>
                <span style={specHeaderValueStyle}>
                  Current Build Phase
                  {currentPhase?.name && (
                    <small style={specPhaseNameStyle}>{currentPhase.name}</small>
                  )}
                </span>
              </div>

              {vehicleSpecRows.map((row) => {
                const isDifferent =
                  row.oemValue &&
                  row.currentValue &&
                  row.oemValue !== row.currentValue;

                return (
                  <div key={row.key} style={specRowStyle}>
                    <span style={specLabelStyle}>{row.label}</span>
                    <span style={specOemValueStyle}>{row.oemValue || "—"}</span>
                    <span
                      style={
                        isDifferent
                          ? specCurrentValueChangedStyle
                          : specCurrentValueStyle
                      }
                    >
                      {row.currentValue || "—"}
                    </span>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        <section style={cardStyle}>
          <h2 style={sectionTitleStyle}>Build Statistics</h2>

          <div style={statsGridStyle}>
            {statistics.map((stat) => {
              const Icon = stat.icon;
              const cardContent = (
                <>
                  <Icon aria-hidden="true" style={statIconStyle} />
                  <span style={statLabelStyle}>{stat.label}</span>
                  <strong style={statValueStyle}>{stat.value}</strong>
                  {stat.cta && <span style={statCtaStyle}>{stat.cta}</span>}
                </>
              );

              if (stat.href) {
                return (
                  <Link
                    key={stat.label}
                    to={stat.href}
                    style={investmentStatCardStyle}
                  >
                    {cardContent}
                  </Link>
                );
              }

              return (
                <div key={stat.label} style={statCardStyle}>
                  {cardContent}
                </div>
              );
            })}
          </div>
        </section>

        <section style={cardStyle}>
          <button
            type="button"
            onClick={() => setModsOpen(!modsOpen)}
            aria-expanded={modsOpen}
            style={sectionToggleStyle}
          >
            <span style={sectionTitleStyle}>Mod List</span>
            {modsOpen ? (
              <FiChevronUp aria-hidden="true" />
            ) : (
              <FiChevronDown aria-hidden="true" />
            )}
          </button>

          {modsOpen && (
            <div style={sectionBodyStyle}>
              {car.mods?.length > 0 ? (
                Object.entries(groupedMods).map(([category, mods]) => {
                  const categoryOpen = openCategories[category] !== false;

                  return (
                    <div key={category} style={modCategoryStyle}>
                      <button
                        type="button"
                        onClick={() => toggleCategory(category)}
                        aria-expanded={categoryOpen}
                        style={categoryToggleStyle}
                      >
                        <span>
                          {category} ({mods.length})
                        </span>
                        {categoryOpen ? (
                          <FiChevronUp aria-hidden="true" />
                        ) : (
                          <FiChevronDown aria-hidden="true" />
                        )}
                      </button>

                      {categoryOpen && (
                        <div style={modListStyle}>
                          {mods.map((mod) => {
                            const installDate = mod.installDate || mod.installedDate;
                            const installMileage =
                              mod.installMileage || mod.installedMileage;

                            return (
                              <article
                                key={mod.id || `${mod.partName}-${mod.brand}`}
                                style={modCardStyle}
                              >
                                <div style={modInfoStyle}>
                                  <h3 style={modTitleStyle}>
                                    {mod.partName || "Unnamed Mod"}
                                  </h3>

                                  {mod.brand && (
                                    <p style={modMetaStyle}>{mod.brand}</p>
                                  )}

                                  {installDate && (
                                    <p style={modMetaStyle}>
                                      Installed: {formatLocalDate(installDate)}
                                    </p>
                                  )}

                                  {installMileage && (
                                    <p style={modMetaStyle}>
                                      Mileage: {installMileage}
                                    </p>
                                  )}
                                </div>

                                {mod.link && (
                                  <a
                                    href={mod.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={smallExternalLinkStyle}
                                  >
                                    View Part <FiExternalLink aria-hidden="true" />
                                  </a>
                                )}
                              </article>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })
              ) : legacyModSections.length > 0 ? (
                <div>
                  <button
                    type="button"
                    onClick={() => setLegacyModsOpen(!legacyModsOpen)}
                    aria-expanded={legacyModsOpen}
                    style={categoryToggleStyle}
                  >
                    <span>Build Details</span>
                    {legacyModsOpen ? (
                      <FiChevronUp aria-hidden="true" />
                    ) : (
                      <FiChevronDown aria-hidden="true" />
                    )}
                  </button>

                  {legacyModsOpen &&
                    legacyModSections.map((section) => (
                      <article key={section.title} style={legacySectionStyle}>
                        <h3 style={legacyTitleStyle}>{section.title}</h3>
                        <p style={legacyTextStyle}>{section.value}</p>
                      </article>
                    ))}
                </div>
              ) : (
                <p style={mutedTextStyle}>No mods have been added yet.</p>
              )}
            </div>
          )}
        </section>

        {car.forSale && (
          <section style={saleSectionStyle}>
            <h2 style={sectionTitleStyle}>For Sale</h2>

            <div style={saleGridStyle}>
              {hasText(car.salePrice) && (
                <InfoRow label="Price" value={car.salePrice} />
              )}
              {hasText(car.saleMileage) && (
                <InfoRow label="Mileage" value={car.saleMileage} />
              )}
              {hasText(car.titleStatus) && (
                <InfoRow label="Title Status" value={car.titleStatus} />
              )}
              {hasText(car.preferredContact) && (
                <InfoRow label="Preferred Contact" value={car.preferredContact} />
              )}
            </div>

            {hasText(car.classifiedsLink) && (
              <a
                href={car.classifiedsLink}
                target="_blank"
                rel="noopener noreferrer"
                style={saleLinkStyle}
              >
                View Classifieds <FiExternalLink aria-hidden="true" />
              </a>
            )}
          </section>
        )}

        {galleryImages.length > 0 && (
          <section style={cardStyle}>
            <div style={sectionHeaderStyle}>
              <h2 style={sectionTitleStyle}>Photo Gallery</h2>
              <FiCamera aria-hidden="true" style={sectionIconStyle} />
            </div>

            <div style={galleryGridStyle}>
              {galleryImages.map((image, index) => (
                <button
                  type="button"
                  key={`${image}-${index}`}
                  onClick={() => setFullscreenImage(image)}
                  style={galleryButtonStyle}
                  aria-label={`Open ${vehicleTitle} photo ${index + 1}`}
                >
                  <img
                    src={image}
                    alt={`${vehicleTitle} photo ${index + 1}`}
                    style={galleryImageStyle}
                  />
                </button>
              ))}
            </div>
          </section>
        )}

        <Link
          to={`/user/${profile.username}/car/${car.id}/timeline`}
          style={timelineCtaStyle}
        >
          <span>View Build Timeline</span>
          <span style={timelineArrowStyle}>→</span>
        </Link>
      </div>

      {fullscreenImage && (
        <div style={fullscreenOverlayStyle} onClick={() => setFullscreenImage(null)}>
          <button
            type="button"
            onClick={() => setFullscreenImage(null)}
            style={fullscreenCloseStyle}
            aria-label="Close fullscreen image"
          >
            ×
          </button>
          <img
            src={fullscreenImage}
            alt={`${vehicleTitle} fullscreen`}
            style={fullscreenImageStyle}
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      )}
    </PageLayout>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={infoRowStyle}>
      <span style={infoLabelStyle}>{label}</span>
      <strong style={infoValueStyle}>{value}</strong>
    </div>
  );
}

const getSavedBuildKey = (ownerUsername, savedCarId) =>
  `${String(ownerUsername).toLowerCase()}-${String(savedCarId)}`;

const isSameSavedBuild = (item, savedKey, ownerUsername, savedCarId) => {
  if (typeof item === "string") {
    return item === savedKey;
  }

  return (
    String(item?.username || item?.ownerUsername || "").toLowerCase() ===
      String(ownerUsername).toLowerCase() &&
    String(item?.carId) === String(savedCarId)
  );
};

const hasText = (value) => String(value || "").trim().length > 0;

const getPriceNumber = (price) => {
  if (price === null || price === undefined || price === "") return 0;
  return Number(String(price).replace(/[^0-9.]/g, "")) || 0;
};

const getModInvestment = (mod = {}) => {
  return getPriceNumber(mod.totalCost ?? mod.price);
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0
  }).format(value || 0);
};

const parseLocalDate = (value) => {
  if (!value) return null;

  if (typeof value === "string") {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);

    if (match) {
      return new Date(
        Number(match[1]),
        Number(match[2]) - 1,
        Number(match[3])
      );
    }
  }

  if (value?.toDate) {
    return value.toDate();
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatLocalDate = (value) => {
  const date = parseLocalDate(value);
  if (!date) return "";

  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
};

const formatRelativeUpdatedDate = (value) => {
  const date = parseLocalDate(value);
  if (!date) return "";

  const today = new Date();
  const todayStart = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  const dateStart = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );

  const dayDifference = Math.round(
    (todayStart.getTime() - dateStart.getTime()) / 86400000
  );

  if (dayDifference === 0) return "Updated today";
  if (dayDifference === 1) return "Updated yesterday";
  if (dayDifference >= 2 && dayDifference <= 6) {
    return `Updated ${dayDifference} days ago`;
  }

  return `Updated ${date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric"
  })}`;
};

const getProfileLocation = (profile) => {
  const safeProfile = profile || {};

  if (hasText(safeProfile.location)) {
    return safeProfile.location;
  }

  if (hasText(safeProfile.city) && hasText(safeProfile.state)) {
    return `${safeProfile.city}, ${safeProfile.state}`;
  }

  if (hasText(safeProfile.city)) {
    return safeProfile.city;
  }

  if (hasText(safeProfile.state)) {
    return safeProfile.state;
  }

  return "";
};

const buildStatistics = (car, totalBuildCost = 0, profileUsername = "") => {
  const safeCar = car || {};
  const stats = [];

  if (hasText(safeCar.purchaseDate)) {
    stats.push({
      label: "Purchased",
      value: formatLocalDate(safeCar.purchaseDate),
      icon: FiCalendar
    });
  }

  const installDates =
    safeCar.mods
      ?.map((mod) => parseLocalDate(mod.installDate || mod.installedDate))
      .filter(Boolean)
      .sort((a, b) => a.getTime() - b.getTime()) || [];

  if (installDates.length > 0) {
    stats.push({
      label: "Build Started",
      value: formatLocalDate(installDates[0]),
      icon: FiActivity
    });
  }

  stats.push({
    label: "Total Mods",
    value: String(safeCar.mods?.length || 0),
    icon: FiTool
  });

  if (safeCar.showBuildCost && totalBuildCost > 0 && profileUsername && safeCar.id) {
    stats.push({
      label: "Build Investment",
      value: formatCurrency(totalBuildCost),
      icon: FiDollarSign,
      href: `/user/${profileUsername}/car/${safeCar.id}/investment`,
      cta: "View Breakdown →"
    });
  }

  return stats;
};

const getStatusBadgeStyle = (status) => {
  const normalizedStatus = String(status || "").toLowerCase();

  if (normalizedStatus.includes("sold")) {
    return {
      ...heroStatusBadgeStyle,
      backgroundColor: "#333",
      color: "#ddd",
      border: "1px solid #555"
    };
  }

  if (normalizedStatus.includes("sale")) {
    return {
      ...heroStatusBadgeStyle,
      backgroundColor: "rgba(225,6,0,0.16)",
      color: "#ff4b47",
      border: "1px solid rgba(225,6,0,0.45)"
    };
  }

  if (normalizedStatus.includes("stored") || normalizedStatus.includes("project")) {
    return {
      ...heroStatusBadgeStyle,
      backgroundColor: "rgba(255,255,255,0.1)",
      color: "#f2f2f2",
      border: "1px solid rgba(255,255,255,0.22)"
    };
  }

  return heroStatusBadgeStyle;
};

const heroImageGradient =
  "linear-gradient(90deg, rgba(0,0,0,0.94) 0%, rgba(0,0,0,0.74) 46%, rgba(0,0,0,0.22) 100%)";

const fallbackHeroGradient =
  "linear-gradient(135deg, #151515 0%, #101010 48%, #240707 100%)";

const sharedCardBaseStyle = {
  backgroundColor: "#181818",
  border: "1px solid #2a2a2a",
  borderRadius: "20px",
  padding: "18px",
  boxSizing: "border-box",
  boxShadow: "0 12px 30px rgba(0,0,0,0.16)"
};

const pageShellStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  width: "100%",
  boxSizing: "border-box"
};

const heroStyle = {
  minHeight: "clamp(360px, 58vw, 520px)",
  border: "1px solid #2a2a2a",
  borderRadius: "20px",
  padding: "32px",
  boxSizing: "border-box",
  backgroundSize: "cover",
  backgroundPosition: "center",
  display: "flex",
  alignItems: "flex-end",
  overflow: "hidden",
  boxShadow: "0 12px 30px rgba(0,0,0,0.16)"
};

const heroContentStyle = {
  width: "100%",
  maxWidth: "640px"
};

const heroStatusRowStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: "10px",
  marginBottom: "18px"
};

const heroStatusBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  width: "fit-content",
  backgroundColor: "#e10600",
  color: "white",
  border: "1px solid rgba(255,255,255,0.18)",
  borderRadius: "999px",
  padding: "7px 12px",
  fontSize: "12px",
  fontWeight: 900,
  letterSpacing: "0.7px"
};

const vehicleTitleStyle = {
  margin: 0,
  color: "white",
  fontSize: "clamp(30px, 7vw, 46px)",
  lineHeight: "1.02",
  fontWeight: 850,
  letterSpacing: 0,
  overflowWrap: "anywhere",
  textShadow: "0 5px 24px rgba(0,0,0,0.6)"
};

const heroMetaStyle = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  flexWrap: "wrap",
  marginTop: "22px"
};

const heroMetaItemStyle = {
  backgroundColor: "rgba(15,15,15,0.78)",
  border: "1px solid rgba(255,255,255,0.14)",
  borderRadius: "999px",
  color: "#e8e8e8",
  padding: "8px 11px",
  fontSize: "13px",
  fontWeight: 750,
  display: "inline-flex",
  alignItems: "center",
  gap: "7px",
  backdropFilter: "blur(8px)"
};

const heroMetaIconStyle = {
  color: "#e10600",
  fontSize: "15px"
};

const ownerRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: "9px",
  flexWrap: "wrap",
  marginTop: "22px",
  color: "#d0d0d0",
  fontSize: "15px"
};

const ownerIconStyle = {
  color: "#e10600"
};

const inlineLinkStyle = {
  color: "white",
  fontWeight: 850,
  textDecoration: "none"
};

const updatedTextStyle = {
  color: "#a5a5a5"
};

const actionBarStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
  backgroundColor: "#181818",
  border: "1px solid #2a2a2a",
  borderRadius: "20px",
  overflow: "hidden",
  boxShadow: "0 12px 30px rgba(0,0,0,0.16)"
};

const actionButtonStyle = {
  minHeight: "64px",
  backgroundColor: "transparent",
  color: "white",
  border: "none",
  borderRight: "1px solid #2a2a2a",
  display: "flex",
  flexDirection: "column",
  gap: "5px",
  justifyContent: "center",
  alignItems: "center",
  fontWeight: 800,
  cursor: "pointer",
  fontSize: "13px",
  lineHeight: "1.1"
};

const actionLinkStyle = {
  ...actionButtonStyle,
  textDecoration: "none",
  boxSizing: "border-box"
};

const actionIconStyle = {
  color: "#d7d7d7",
  fontSize: "20px"
};

const actionIconActiveStyle = {
  color: "#e10600",
  fontSize: "20px"
};

const cardStyle = {
  ...sharedCardBaseStyle
};

const compactCardStyle = {
  ...sharedCardBaseStyle,
  padding: "16px 18px"
};

const phaseHeaderStyle = {
  display: "flex",
  alignItems: "center",
  gap: "9px",
  marginBottom: "10px"
};

const phaseLabelStyle = {
  color: "#e10600",
  fontSize: "12px",
  fontWeight: 900,
  letterSpacing: "1px",
  textTransform: "uppercase"
};

const sectionHeaderStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "14px",
  marginBottom: "14px"
};

const sectionTitleStyle = {
  margin: 0,
  color: "#e10600",
  fontSize: "21px",
  lineHeight: "1.2",
  fontWeight: 900
};

const sectionIconStyle = {
  color: "#e10600",
  fontSize: "20px",
  flexShrink: 0
};

const phaseMenuWrapStyle = {
  backgroundColor: "#111",
  border: "1px solid #262626",
  borderRadius: "14px",
  padding: "12px",
  boxSizing: "border-box"
};

const aboutTextStyle = {
  margin: "14px 0 0",
  color: "#e8e8e8",
  lineHeight: "1.7",
  whiteSpace: "pre-line",
  textAlign: "left",
  overflowWrap: "anywhere"
};

const specComparisonStyle = {
  marginTop: "14px",
  backgroundColor: "#111",
  border: "1px solid #292929",
  borderRadius: "16px",
  overflow: "hidden"
};

const specHeaderRowStyle = {
  display: "grid",
  gridTemplateColumns: "minmax(82px, 0.75fr) minmax(0, 1fr) minmax(0, 1fr)",
  gap: "10px",
  padding: "12px",
  borderBottom: "1px solid #292929",
  backgroundColor: "#151515"
};

const specHeaderLabelStyle = {
  color: "#858585",
  fontSize: "12px",
  fontWeight: "900",
  textTransform: "uppercase",
  letterSpacing: "0.6px"
};

const specHeaderValueStyle = {
  color: "#ddd",
  fontSize: "12px",
  fontWeight: "900",
  textTransform: "uppercase",
  letterSpacing: "0.6px",
  display: "flex",
  flexDirection: "column",
  gap: "3px",
  overflowWrap: "anywhere"
};

const specPhaseNameStyle = {
  color: "#888",
  fontSize: "11px",
  textTransform: "none",
  letterSpacing: 0
};

const specRowStyle = {
  display: "grid",
  gridTemplateColumns: "minmax(82px, 0.75fr) minmax(0, 1fr) minmax(0, 1fr)",
  gap: "10px",
  padding: "12px",
  borderBottom: "1px solid #242424",
  alignItems: "start"
};

const specLabelStyle = {
  color: "#888",
  fontSize: "13px",
  fontWeight: "800",
  overflowWrap: "anywhere"
};

const specOemValueStyle = {
  color: "#b5b5b5",
  fontSize: "14px",
  lineHeight: "1.45",
  overflowWrap: "anywhere"
};

const specCurrentValueStyle = {
  color: "white",
  fontSize: "14px",
  fontWeight: "800",
  lineHeight: "1.45",
  overflowWrap: "anywhere"
};

const specCurrentValueChangedStyle = {
  ...specCurrentValueStyle,
  color: "#fff",
  borderLeft: "3px solid #e10600",
  paddingLeft: "8px"
};

const statsGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(145px, 1fr))",
  gap: "12px",
  marginTop: "14px"
};

const statCardStyle = {
  backgroundColor: "#111",
  border: "1px solid #292929",
  borderRadius: "16px",
  padding: "16px",
  minHeight: "130px",
  display: "flex",
  flexDirection: "column",
  gap: "10px",
  boxSizing: "border-box"
};

const investmentStatCardStyle = {
  ...statCardStyle,
  border: "1px solid rgba(225,6,0,0.58)",
  textDecoration: "none"
};

const statIconStyle = {
  color: "#e10600",
  fontSize: "21px",
  marginBottom: "2px"
};

const statLabelStyle = {
  color: "#858585",
  fontSize: "12px",
  fontWeight: 800,
  letterSpacing: "0.2px"
};

const statValueStyle = {
  color: "white",
  fontSize: "22px",
  fontWeight: 900,
  overflowWrap: "anywhere",
  lineHeight: "1.15"
};

const statCtaStyle = {
  color: "#e10600",
  fontSize: "13px",
  fontWeight: 900,
  marginTop: "auto"
};

const sectionToggleStyle = {
  width: "100%",
  backgroundColor: "transparent",
  border: "none",
  color: "white",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "14px",
  padding: 0,
  cursor: "pointer",
  textAlign: "left"
};

const sectionBodyStyle = {
  marginTop: "14px"
};

const modCategoryStyle = {
  backgroundColor: "#111",
  border: "1px solid #262626",
  borderRadius: "16px",
  padding: "14px",
  marginBottom: "10px"
};

const categoryToggleStyle = {
  width: "100%",
  backgroundColor: "transparent",
  border: "none",
  color: "white",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "4px 0",
  fontSize: "16px",
  fontWeight: 900,
  cursor: "pointer",
  textAlign: "left"
};

const modListStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "10px",
  marginTop: "12px"
};

const modCardStyle = {
  backgroundColor: "#191919",
  border: "1px solid #2f2f2f",
  borderRadius: "14px",
  padding: "14px",
  display: "flex",
  justifyContent: "space-between",
  gap: "14px",
  flexWrap: "wrap"
};

const modInfoStyle = {
  minWidth: 0,
  flex: "1 1 220px"
};

const modTitleStyle = {
  margin: 0,
  color: "white",
  fontSize: "16px",
  overflowWrap: "anywhere"
};

const modMetaStyle = {
  color: "#a8a8a8",
  margin: "6px 0 0",
  fontSize: "14px",
  overflowWrap: "anywhere"
};

const smallExternalLinkStyle = {
  color: "#e10600",
  fontWeight: 900,
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  textDecoration: "none",
  alignSelf: "center"
};

const legacySectionStyle = {
  backgroundColor: "#111",
  border: "1px solid #292929",
  borderRadius: "14px",
  padding: "14px",
  marginTop: "10px"
};

const legacyTitleStyle = {
  margin: "0 0 8px",
  color: "white",
  fontSize: "16px"
};

const legacyTextStyle = {
  margin: 0,
  color: "#d8d8d8",
  lineHeight: "1.65",
  whiteSpace: "pre-line",
  overflowWrap: "anywhere"
};

const mutedTextStyle = {
  color: "#9a9a9a",
  lineHeight: "1.6"
};

const saleSectionStyle = {
  ...sharedCardBaseStyle,
  border: "1px solid rgba(225,6,0,0.42)"
};

const saleGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
  gap: "12px",
  marginTop: "14px"
};

const infoRowStyle = {
  backgroundColor: "#111",
  border: "1px solid #292929",
  borderRadius: "14px",
  padding: "14px"
};

const infoLabelStyle = {
  display: "block",
  color: "#8c8c8c",
  fontSize: "13px",
  fontWeight: 800,
  marginBottom: "6px"
};

const infoValueStyle = {
  color: "white",
  overflowWrap: "anywhere"
};

const saleLinkStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  color: "#e10600",
  fontWeight: 900,
  textDecoration: "none",
  marginTop: "16px"
};

const galleryGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: "14px",
  marginTop: "14px"
};

const galleryButtonStyle = {
  border: "1px solid #303030",
  backgroundColor: "#111",
  borderRadius: "16px",
  overflow: "hidden",
  padding: 0,
  cursor: "pointer",
  aspectRatio: "4 / 3"
};

const galleryImageStyle = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block"
};

const timelineCtaStyle = {
  backgroundColor: "#181818",
  border: "1px solid #2a2a2a",
  borderRadius: "20px",
  padding: "18px",
  color: "white",
  textDecoration: "none",
  fontSize: "18px",
  fontWeight: 900,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "14px",
  boxShadow: "0 12px 30px rgba(0,0,0,0.16)"
};

const timelineArrowStyle = {
  color: "#e10600",
  fontSize: "24px"
};

const stateCardStyle = {
  ...sharedCardBaseStyle,
  padding: "40px 20px",
  color: "white",
  textAlign: "center",
  fontWeight: 800
};

const toastStyle = {
  position: "fixed",
  left: "50%",
  bottom: "calc(110px + env(safe-area-inset-bottom))",
  transform: "translateX(-50%)",
  backgroundColor: "#e10600",
  color: "white",
  borderRadius: "999px",
  padding: "12px 16px",
  fontWeight: 900,
  zIndex: 50000,
  boxShadow: "0 16px 40px rgba(0,0,0,0.36)"
};

const fullscreenOverlayStyle = {
  position: "fixed",
  inset: 0,
  backgroundColor: "rgba(0,0,0,0.92)",
  zIndex: 60000,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: "20px",
  boxSizing: "border-box"
};

const fullscreenCloseStyle = {
  position: "absolute",
  top: "18px",
  right: "18px",
  width: "44px",
  height: "44px",
  borderRadius: "999px",
  border: "1px solid #444",
  backgroundColor: "#111",
  color: "white",
  fontSize: "28px",
  cursor: "pointer"
};

const fullscreenImageStyle = {
  maxWidth: "100%",
  maxHeight: "100%",
  objectFit: "contain",
  borderRadius: "16px"
};

export default CarPage;
